"use client";

import { Check, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reassurancePoints = ["No-obligation estimates", "Professional site assessment", "Premium material consultation"];
const treatmentOptions = ["Metal Roofing", "Architectural Cladding", "James Hardie Siding", "Stone Veneer", "Acrylic Stucco", "General Inquiry"];
const visitTimes = [{
  value: "weekday-morning",
  label: "Weekday Morning"
}, {
  value: "weekday-afternoon",
  label: "Weekday Afternoon"
}, {
  value: "saturday",
  label: "Saturday"
}];
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 md:p-16 border border-border bg-white shadow-xl rounded-lg text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 mb-8 rounded-lg bg-primary/10">
          <Check className="w-10 h-10 text-primary" />
        </div>
        <h3 className="font-default text-3xl text-secondary mb-6 leading-tight font-bold tracking-tight">
          Request Received.
        </h3>
        <p className="font-default text-base text-muted-foreground mb-10 max-w-md mx-auto">
          {successMessage || "Thank you! Your request has been submitted successfully. We will contact you shortly."}
        </p>
        <Button onClick={() => {
        resetForm();
      }} variant="outline" className="w-full sm:w-auto h-14 min-h-[48px] px-8 uppercase tracking-widest text-xs font-bold rounded-lg border-secondary text-secondary hover:bg-secondary hover:text-white transition-all">
          Submit Another Request
        </Button>
      </div>;
  }
  return <div className="p-6 sm:p-8 md:p-12 lg:p-16 border border-border bg-white shadow-2xl rounded-lg relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1 h-full bg-primary/20" />
      
      <Form onSubmit={handleSubmit} className="space-y-8 sm:space-y-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          <FormField control={control} name="full_name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Min 2 characters"
          }
        }} render={({
          field
        }) => <FormItem className="w-full">
                <FormLabel className="font-mono text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground">
                  Full Name *
                </FormLabel>
                <FormControl>
                  <Input placeholder="Your Name" {...field} className="h-14 w-full bg-transparent border-0 border-b-2 border-border rounded-lg px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/50 text-base md:text-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          pattern: {
            value: /^(?:[^\s@]+@[^\s@]+\.[^\s@]+)?$/,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem className="w-full">
                <FormLabel className="font-mono text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" inputMode="email" placeholder="Your Email (Optional)" {...field} className="h-14 w-full bg-transparent border-0 border-b-2 border-border rounded-lg px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/50 text-base md:text-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          <FormField control={control} name="phone" rules={{
          required: "Phone number is required"
        }} render={({
          field
        }) => <FormItem className="w-full">
                <FormLabel className="font-mono text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground">
                  Phone Number *
                </FormLabel>
                <FormControl>
                  <Input type="tel" inputMode="tel" placeholder="Your Phone Number" {...field} className="h-14 w-full bg-transparent border-0 border-b-2 border-border rounded-lg px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/50 text-base md:text-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="postal_code" rules={{
          required: "Postal code is required"
        }} render={({
          field
        }) => <FormItem className="w-full">
                <FormLabel className="font-mono text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground">
                  Postal Code *
                </FormLabel>
                <FormControl>
                  <Input type="text" autoComplete="postal-code" placeholder="Your Postal Code" {...field} className="h-14 w-full bg-transparent border-0 border-b-2 border-border rounded-lg px-0 focus-visible:ring-0 focus-visible:border-primary transition-all placeholder:text-muted-foreground/50 text-base md:text-sm" />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem className="w-full">
              <FormLabel className="font-mono text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground">
                Project Type *
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger className="h-14 w-full bg-transparent border-0 border-b-2 border-border rounded-lg px-0 focus-visible:ring-0 focus-visible:border-primary transition-all text-base md:text-sm">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border border-border rounded-lg">
                  {treatmentOptions.map((opt, i) => <SelectItem key={opt} data-index={i} value={opt} className="font-default text-secondary focus:bg-accent min-h-[44px]">
                      {opt}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="preferred_date" render={({
        field
      }) => <FormItem className="w-full">
              <FormLabel className="font-mono text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground mb-4 block">
                Preferred Contact Time
              </FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value || ""} className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 w-full">
                  {visitTimes.map((time, i) => <div key={time.value} data-index={i} className="flex w-full sm:w-auto">
                      <RadioGroupItem value={time.value} id={`visit-${time.value}`} className="peer sr-only" />
                      <Label htmlFor={`visit-${time.value}`} className="cursor-pointer min-h-[48px] w-full sm:w-auto flex items-center justify-center px-6 py-3 border border-border bg-white text-secondary font-mono text-[10px] uppercase font-bold hover:border-primary hover:text-primary peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-all rounded-lg tracking-widest text-center">
                        {time.label}
                      </Label>
                    </div>)}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem className="w-full">
              <FormLabel className="font-mono text-[10px] uppercase font-bold tracking-[0.2em] text-muted-foreground">
                Project Details
              </FormLabel>
              <FormControl>
                <Textarea rows={4} placeholder="Tell us about your renovation needs..." {...field} className="w-full bg-transparent border-0 border-b-2 border-border rounded-lg px-0 focus-visible:ring-0 focus-visible:border-primary transition-all resize-none min-h-[120px] placeholder:text-muted-foreground/50 text-base md:text-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="space-y-6">
          <FormField control={control} name="consent" rules={{
          validate: v => v === true || "Consent required to proceed."
        }} render={({
          field
        }) => <FormItem>
                <div className="flex items-start gap-4 min-h-[48px]">
                  <FormControl>
                    <Checkbox id="consent" checked={field.value} onCheckedChange={field.onChange} className="mt-1 rounded-lg size-5 shrink-0" />
                  </FormControl>
                  <Label htmlFor="consent" className="font-default text-sm text-muted-foreground cursor-pointer leading-relaxed">
                    I agree to be contacted by MetalLine Exteriors regarding my project inquiry.
                  </Label>
                </div>
                <FormMessage className="text-xs font-mono uppercase tracking-wider" />
              </FormItem>} />

          <p className="text-sm text-muted-foreground leading-relaxed">
            We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
            <Link to="/PrivacyPolicyPage" className="text-primary underline underline-offset-4 hover:text-primary/80 transition-colors font-medium">
              Privacy Policy
            </Link>
            .
          </p>

          {submitError && <div className="p-4 border border-destructive/20 bg-destructive/5 rounded-lg">
              <p className="text-destructive font-default text-sm">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full min-h-[52px] h-auto px-8 py-4 font-mono text-xs uppercase font-bold tracking-[0.2em] shadow-xl hover:opacity-90 transition-all rounded-lg group">
            {isSubmitting ? "Processing..." : <span className="flex items-center justify-center gap-3 w-full">
                Request Estimate <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>}
          </Button>
        </div>
      </Form>
    </div>;
}
export default function ContactSection() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="premiumcontactform" className="bg-background text-foreground relative overflow-hidden py-16 sm:py-24 lg:py-40 px-4 sm:px-6">
      <div className="absolute top-12 right-12 hidden lg:block" />

      <div className="absolute left-6 top-1/2 -translate-y-1/2 hidden xl:block">
        <p className="font-mono text-[10px] text-muted-foreground tracking-[0.5em] uppercase -rotate-90 origin-left whitespace-nowrap opacity-30">
          ACM PANEL // GRADE A // EXTERIOR SPEC
        </p>
      </div>

      <div className="max-w-full mx-auto px-2 sm:px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-32 lg:self-start transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-primary" />
              <p className="font-mono text-[10px] uppercase font-bold tracking-[0.3em] text-primary">
                Get Started
              </p>
            </div>

            <h1 className="font-default font-bold text-secondary mb-10 leading-[1.1] tracking-tighter max-w-full" style={{
            fontSize: "clamp(2.5rem, 5vw, 4.5rem)"
          }}>
              Start Your
              <br />
              Exterior
              <br />
              <span className="text-primary">Transformation.</span>
            </h1>

            <p className="font-default text-muted-foreground mb-12 leading-relaxed text-lg max-w-2xl">
              Ready to upgrade your home? Our premium exterior solutions in Calgary are designed for durability and aesthetic excellence.
            </p>

            <ul className="space-y-6 mb-16">
              {reassurancePoints.map((point, i) => <li key={point} data-index={i} className="flex items-center gap-5 group">
                  <div className="w-2 h-2 bg-primary rounded-lg group-hover:scale-125 transition-transform" />
                  <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">{point}</span>
                </li>)}
            </ul>

            <div className="pt-10 border-t border-border">
              <div className="flex items-center gap-6">
                <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Prefer to talk?</p>
                  <Link to="tel:+15878884223" className="text-xl font-bold text-secondary hover:text-primary transition-colors tracking-tight">
                    (587) 888-4223
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <FormProvider formId="about_estimate_form_about" formKey="about_estimate_form_about" sectionName="premiumcontactform" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            postal_code: "",
            service_type: "",
            preferred_date: "",
            additional_details: "",
            consent: false
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email: {
              pattern: "email"
            },
            phone: {
              required: true
            },
            postal_code: {
              required: true
            },
            service_type: {
              required: true
            }
          }} submitText="Request Estimate" successMessage="Thank you! Your request has been submitted successfully. We will contact you shortly." errorMessage="Something went wrong. Please try again or call us directly.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}